import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retryWhen, mergeMap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) {}

  private handleError(error: any) {
    console.error('API Error from service:', error);
    return throwError(() => error);
  }

  /** Retry + error handling for idempotent reads (GET). */
  private withRetry<T>(observable: Observable<T>): Observable<T> {
    return observable.pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, attempt) => {
            if (
              error instanceof HttpErrorResponse &&
              error.status >= 400 &&
              error.status < 500
            ) {
              return throwError(() => error);
            }
            if (attempt >= 2) {
              return throwError(() => error);
            }
            return timer(1000);
          }),
        ),
      ),
      catchError(this.handleError),
    );
  }

  /** Error handling only — NO retry. Used for mutating calls (POST/PUT/DELETE). */
  private withErrorHandling<T>(observable: Observable<T>): Observable<T> {
    return observable.pipe(catchError(this.handleError));
  }

  get<T>(url: string, params?: any): Observable<T> {
    return this.withRetry(this.http.get<T>(url, { params }));
  }

  post<T>(url: string, data: any): Observable<T>;
  post(
    url: string,
    data: any,
    options: { responseType: 'text' },
  ): Observable<string>;
  post<T>(
    url: string,
    data: any,
    options?: { responseType?: 'json' | 'text' },
  ): Observable<T | string> {
    if (options?.responseType === 'text') {
      return this.withErrorHandling(
        this.http.post(url, data, { responseType: 'text' as const }),
      );
    }
    return this.withErrorHandling(this.http.post<T>(url, data));
  }

  postEvent<T>(
    url: string,
    data: any,
    meta?: {
      headers?: HttpHeaders;
      params?: any;
      reportProgress?: boolean;
    },
  ): Observable<HttpEvent<T>> {
    const options = {
      ...meta,
      observe: 'events' as const,
      reportProgress: true,
    };
    return this.withErrorHandling(this.http.post<T>(url, data, options));
  }

  put<T>(url: string, data: any): Observable<T> {
    return this.withErrorHandling(this.http.put<T>(url, data));
  }

  delete<T>(url: string, params?: any): Observable<T> {
    return this.withErrorHandling(this.http.delete<T>(url, { params }));
  }
}
